import { Component } from '@angular/core';
import { DatePipe, Location } from '@angular/common';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { emsCompetition, emsTeam, emsVetoAction, emsVetoPool } from 'src/models/emsModels';
import { NotificationBar } from 'src/services/notification.service';
import { environment } from 'src/environment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RedirectDialogComponent } from '../redirect-dialog/redirect-dialog.component';

@Component({
  selector: 'app-create-host',
  templateUrl: './veto-create.component.html',
  styleUrl: './veto-create.component.scss',
})
export class VetoCreateComponent {
  
  notificationBar: NotificationBar;
  vetoGroup = new FormGroup({
    competitionControl: new FormControl('', [Validators.required]),
    teamsControl: new FormControl<string[]>([], [Validators.required, this.exactlyTwoSelected]),
    formatControl: new FormControl('', [Validators.required]),
    mapPoolControl: new FormControl([], [this.exactlySevenSelected]),
    dateControl: new FormControl('', [Validators.required]),
  });
  workspaceID = "";
  competitionID = "";
  competitions = Array<emsCompetition>();
  teams = Array<emsTeam>();
  isProcessing = false;
  placeholderImage = environment.placeholderImage;
  placeholderTeamLogo = environment.placeholderTeamLogo;
  minDate = new Date();
  maxDate = new Date();
  competitionsDBRef: any;
  teamsDBRef: any;

  constructor(private title: Title, private location: Location, private snackBar: MatSnackBar, private db: AngularFireDatabase, public dialog: MatDialog) {
    this.notificationBar = new NotificationBar(snackBar);
  }

  async ngOnInit() {
    this.workspaceID  = this.location.path().split("/")[1];
    this.title.setTitle("Create Veto - EsportsMS");
    this.loadCompetitions();
    this.loadTeams();
    this.loadDefaultMapPool();
    this.minDate.setDate(this.minDate.getDate() - 1);
    this.maxDate.setDate(this.maxDate.getDate() + 90);
  }

  ngOnDestroy() {
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
  }

  loadDefaultMapPool() {
    this.vetoGroup.controls.mapPoolControl.setValue(['Abyss', 'Ascent', 'Haven', 'Lotus', 'Split', 'Summit', 'Sunset']);
  }

  exactlySevenSelected(control: FormControl) {
    const value = control.value;
    if (!value || value.length != 7) {
      return { invalidSelectionCount: true };
    }
    return null;
  }

  exactlyTwoSelected(control: FormControl) {
    const value = control.value;
    if (!value || value.length != 2) {
      return { invalidSelectionCount: true };
    }
    return null;
  }

  loadCompetitions() {
    this.competitionsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions').orderByChild('workspace').equalTo(this.workspaceID);
    this.competitionsDBRef.on('value', x => {
      this.competitions = new Array<emsCompetition>();
      x.forEach( x => {
        var competition = x.toJSON() as emsCompetition;
        competition.uid = x.key;
        if (competition.active == true) {
          this.competitions.push(competition);
        }
      });
      this.competitions.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  getSelectedCompetition(uid: string) {
    return this.competitions.find(competition => competition.uid == uid);
  }

  loadTeams() {
    this.teamsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/teams').orderByChild('workspace').equalTo(this.workspaceID);
    this.teamsDBRef.on('value', x => {
      this.teams = new Array<emsTeam>();
      x.forEach( x => {
        var team = x.toJSON() as emsTeam;
        team.uid = x.key;
        this.teams.push(team);
      });
      this.teams.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  getSelectedTeams(uids: string[]) {
    var teams = new Array<emsTeam>();
    if (uids) {
      uids.forEach(uid => {
        var team = this.teams.find(team => team.uid == uid);
        teams.push(team);
      })
    }
    return teams;
  }

  generateLobbyCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  async createVeto() {
    var lobbyCode = this.generateLobbyCode();
    const bo1Actions: emsVetoAction[] = [
      { executor: "admin", type: "selectStartingTeam", info: "selecting starting team", log: ""},
      { executor: "startingTeam", type: "selectTeamAB", info: "selecting team", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "system", type: "decider", info: "processing", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
    ];
    const bo3Actions: emsVetoAction[] = [
      { executor: "admin", type: "selectStartingTeam", info: "selecting starting team", log: ""},
      { executor: "startingTeam", type: "selectTeamAB", info: "selecting team", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "teamA", type: "pick", info: "picking a map", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
      { executor: "teamB", type: "pick", info: "picking a map", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "system", type: "decider", info: "processing", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
    ];
    const bo5Actions: emsVetoAction[] = [
      { executor: "admin", type: "selectStartingTeam", info: "selecting starting team", log: ""},
      { executor: "startingTeam", type: "selectTeamAB", info: "selecting team", log: ""},
      { executor: "teamA", type: "ban", info: "banning a map", log: ""},
      { executor: "teamB", type: "ban", info: "banning a map", log: ""},
      { executor: "teamA", type: "pick", info: "picking a map", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
      { executor: "teamB", type: "pick", info: "picking a map", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
      { executor: "teamA", type: "pick", info: "picking a map", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
      { executor: "teamB", type: "pick", info: "picking a map", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
      { executor: "system", type: "decider", info: "processing", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
    ];
    const bo5FinalActions: emsVetoAction[] = [
      { executor: "admin", type: "selectStartingTeam", info: "selecting starting team", log: ""},
      { executor: "startingTeam", type: "selectTeamAB", info: "selecting team", log: ""},
      { executor: "startingTeam", type: "ban", info: "banning a map", log: ""},
      { executor: "startingTeam", type: "ban", info: "banning a map", log: ""},
      { executor: "teamA", type: "pick", info: "picking a map", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
      { executor: "teamB", type: "pick", info: "picking a map", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
      { executor: "teamA", type: "pick", info: "picking a map", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
      { executor: "teamB", type: "pick", info: "picking a map", log: ""},
      { executor: "teamA", type: "side", info: "selecting the side", log: ""},
      { executor: "system", type: "decider", info: "processing", log: ""},
      { executor: "teamB", type: "side", info: "selecting the side", log: ""},
    ];
    var formatActions = new Array<emsVetoAction>;
    if (this.vetoGroup.controls.formatControl.value == "bo1") {
      formatActions = bo1Actions;
    }
    else if (this.vetoGroup.controls.formatControl.value == "bo3") {
      formatActions = bo3Actions;
    }
    else if (this.vetoGroup.controls.formatControl.value == "bo5") {
      formatActions = bo5Actions;
    }
    else if (this.vetoGroup.controls.formatControl.value == "bo5Final") {
      formatActions = bo5FinalActions;
    }
    else {
      this.notificationBar.show("Invalid Format!")
      return;
    }
    var teamOne = this.teams.find(x => x.uid == this.vetoGroup.controls.teamsControl.value[0]).uid;
    var teamtwo = this.teams.find(x => x.uid == this.vetoGroup.controls.teamsControl.value[1]).uid;
    var selectedMaps: string[] = this.vetoGroup.controls.mapPoolControl.value;
    var mapPool = new Array<emsVetoPool>();
    var mapPool = selectedMaps.map(mapName => ({
      name: mapName,
      available: true
    }));
    var datePipe = new DatePipe('en-US');
    var selectedDate = datePipe.transform(this.vetoGroup.controls.dateControl.value, 'yyyy-MM-dd');
    var vetoDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/vetos/lobbies');
    var newVetoRef = vetoDBRef.push();
    newVetoRef.set({
      'workspace': this.workspaceID,
      'lobbyCode': lobbyCode,
      'competition': this.vetoGroup.controls.competitionControl.value,
      'teamOne': teamOne,
      'teamTwo': teamtwo,
      'pool': mapPool,
      'actions': formatActions,
      'currentAction': 0,
      'concluded': '',
      'date': selectedDate,
    });
    this.notificationBar.show("Veto created!")
    this.vetoGroup.reset();
    this.vetoGroup.controls.competitionControl.setErrors(null);
    this.vetoGroup.controls.teamsControl.setErrors(null);
    this.vetoGroup.controls.formatControl.setErrors(null);
    this.vetoGroup.controls.mapPoolControl.setErrors(null);
    this.vetoGroup.controls.dateControl.setErrors(null);
    this.loadDefaultMapPool();
    var dialogRef = this.dialog.open(RedirectDialogComponent, {
      data: {
        message: "Lobby Code: " + lobbyCode,
        redirect: "/veto?lobby=" + lobbyCode,
      }
    });
  }
}
